import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CardGroup } from './api'
import { deleteCardGroupOverride, getCardGroup, setCardGroupOverride } from './endpoints'
import { mapCardGroupResponseToCardGroupData } from './mappers'

const QueryKeys = {
  CardGroup: (customerId: string) => ['card-group', customerId] as const,
  RemoveCardGroupOverride: (customerId: string) => ['remove-card-group-override', customerId] as const,
}

interface InvalidateCardGroupParams {
  queryClient: QueryClient
  customerId: string
}

export const invalidateCardGroup = ({ queryClient, customerId }: InvalidateCardGroupParams) => {
  queryClient.invalidateQueries({ queryKey: QueryKeys.CardGroup(customerId) })
}

export const useCardGroup = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.CardGroup(customerId),
    queryFn: () => getCardGroup(customerId),
  })
}

export const useRemoveCardGroupOverride = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.RemoveCardGroupOverride(customerId),
    mutationFn: () => deleteCardGroupOverride(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.CardGroup(customerId) })
    },
  })
}

export const useSetCardGroupOverride = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.RemoveCardGroupOverride(customerId),
    mutationFn: (group: CardGroup) => setCardGroupOverride(customerId, group),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(QueryKeys.CardGroup(customerId), mapCardGroupResponseToCardGroupData(data))
    },
  })
}
